import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import axios from "axios";
import uuid from "react-uuid";

import { IoMdClose } from "react-icons/io";

import {
  ProjectCloseButton,
  ProjectCreateSecContainer,
  ProjectSection,
} from "./component/common/styled/project";

import InputField from "./component/projectCreateSecPage/inputField";
import Preview from "./component/projectCreateSecPage/preview";
import Editor from "./component/projectCreateSecPage/editor";

import { useModal } from "../../../hooks/useModal";
import useUploadFiles from "../../../hooks/useUploadFiles";

export default function ProjectCreateSec() {
  const navigate = useNavigate();
  const location = useLocation();

  const projectInfo = location?.state?.projectInfo;
  const eventInfo = location?.state?.eventInfo;
  const navigatePath = location?.state?.navigatePath;

  const { modal, openModal, closeModal } = useModal();
  const { uploadFiles, uploadEmbedded, dataURItoBlob, readFileAsDataURL } = useUploadFiles();

  const user = useSelector((state) => state?.user?.data[0]);

  const ckeditorInstanceRef = useRef(null);

  const [isAddDesc, setIsAddDesc] = useState(false);
  // 초기 폼 데이터 생성
  const getInitialFormData = useCallback(
    () => ({
      orgNo: null,
      gbn: "C",
      mberNo: user.mberNo,
      projectNm: "",
      projectDesc: "",
      title: "",
      content: "",
      publicYn: "Y",
      completeYn: "N",
      delYn: "N",
      createMberId: user.userName,
      updateMberId: user.userName,
    }),
    [user.mberNo, user.userName],
  );

  const [formData, setFormData] = useState(getInitialFormData);
  const [viewType, setViewType] = useState(0);
  const [focusState, setFocusState] = useState({
    projectNm: true,
    projectDesc: false,
    title: false,
    content: false,
  });
  const [duplProjectNm, setDuplProjectNm] = useState(false);
  const [files, setFiles] = useState([]);
  const [origin, setOrigin] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);

  const transformValue = viewType * -100;

  // 포커스 상태 관리
  const handleFocus = useCallback((name) => {
    setFocusState((prev) => {
      const updated = Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === name;
        return acc;
      }, {});
      return updated;
    });
  }, []);

  const handleBlur = useCallback((name) => {
    setFocusState((prev) => ({ ...prev, [name]: false }));
  }, []);

  useEffect(() => {
    if (projectInfo) {
      setFormData({ ...projectInfo });
      if (projectInfo.fileList) {
        setFiles(
          [...projectInfo.fileList].map((item) => ({ ...item, name: item.fileNm, id: uuid() })),
        );
      }
      if (projectInfo?.projectDesc) {
        setIsAddDesc(true);
      }
    }
  }, []);

  // 애니메이션
  const handleViewTypeChange = () => {
    setViewType(viewType === 0 ? 1 : 0);
  };

  // 입력값 변경 처리
  const changeInput = useCallback(
    (e) => {
      const { name, type, value, checked } = e.target;

      if (!name) return;

      if (name === "projectNm" && duplProjectNm) {
        setDuplProjectNm(false);
      }

      if (name === "orgNo" && projectInfo) {
        toast.error("프로젝트 생성 후에는 수정이 불가합니다.");
        e.target.value = formData.orgNo;
        return;
      }

      const newValue = type === "checkbox" ? (checked ? "N" : "Y") : value;
      setFormData((prev) => ({ ...prev, [name]: newValue }));
    },
    [duplProjectNm, projectInfo, formData.orgNo],
  );

  // 서버에 반영된 파일 삭제
  const handleServerDeleteFile = async (data) => {
    if (!data.length) return;

    try {
      const deleteRequests = data.map((item) =>
        axios.put("/api/file", {
          delYn: "Y",
          projectFileNo: item.projectFileNo,
          projectNo: item.projectNo,
          gbn: item.gbn,
          refeNo: item.refeNo,
          mberNo: user.mberNo,
          fileType: item.fileType,
        }),
      );

      await Promise.all(deleteRequests);
    } catch (error) {
      console.error(error);
    }
  };

  // 유효성 검사 및 저장 확인
  const checkValidation = useCallback(() => {
    if (!formData.projectNm.trim()) {
      toast.error("프로젝트명을 입력해 주세요.");
      return;
    }

    const isEdit = !!projectInfo;
    const action = isEdit ? "수정" : "생성";

    openModal({
      isOpen: true,
      type: "confirmation",
      title: `프로젝트 ${action}`,
      message: [`프로젝트를 ${action}하시겠습니까?`],
      handleConfirm: saveProject,
      handleCancel: closeModal,
    });
  }, [formData.projectNm, projectInfo, openModal, closeModal]);

  const handleAllUploads = async () => {
    const [updatedContent, uploaded] = await Promise.all([
      uploadEmbedded(formData.content),
      handleUploadFiles(origin),
    ]);

    const uploadedFiles = uploaded.map((f) => ({
      fileNm: f.fileName.split("/").pop(),
      filePath: f.fileName,
    }));

    return { updatedContent, uploadedFiles };
  };

  // 파일 업로드 처리
  const handleUploadFiles = useCallback(
    async (files) => {
      if (!files?.length) return [];

      try {
        const normalizedFiles = files
          .map((file) => {
            if (typeof file === "string" && file.startsWith("data:image/")) {
              return dataURItoBlob(file);
            }
            if (file?.file instanceof File) {
              return file.file;
            }
            return file instanceof File ? file : null;
          })
          .filter(Boolean);

        return await uploadFiles(normalizedFiles);
      } catch (error) {
        console.error("파일 업로드 중 오류 발생:", error);
        toast.error("파일 업로드 중 오류가 발생했습니다.");
        return [];
      }
    },
    [uploadFiles, dataURItoBlob],
  );

  // 모달-확인
  const saveProject = async () => {
    const { updatedContent, uploadedFiles } = await handleAllUploads();

    const url = "/api/project";
    const body = {
      ...formData,
      content: updatedContent,
      projectDesc: isAddDesc ? formData.projectDesc : "",
      title: formData.title || formData.projectNm,
    };

    const res = await axios.put(url, body);
    if (res.data.success) {
      if (uploadedFiles.length) {
        await updateProjectFiles(res.data.rtnModel.projectNo, uploadedFiles);
      }

      if (deletedFiles.length) {
        await handleServerDeleteFile(deletedFiles);
        setDeletedFiles([]);
      }

      toast.success(`프로젝트가 ${projectInfo ? "수정" : "등록"}되었습니다.`);
      navigate(eventInfo ? "/mypage/scheduler/form" : "/mypage/projectList", {
        state: eventInfo
          ? {
              eventInfo: { ...eventInfo, projectNo: res.data.rtnModel.projectNo },
              infoState: "write",
            }
          : {},
      });
    }
  };

  // 프로젝트명 중복 체크
  const checkDuplProjectNm = useCallback(async () => {
    const projectName = formData.projectNm.trim();

    if (!projectName) {
      toast.error("프로젝트 명을 입력해주세요.");
      return;
    }

    try {
      const response = await axios.post("/api/duplProjectNm", {
        gbn: formData.gbn,
        orgNo: formData.orgNo,
        projectNm: projectName,
      });

      if (response.status === 200) {
        const isAvailable = response.data.success;
        setDuplProjectNm(isAvailable);

        const message = isAvailable
          ? "사용 가능한 프로젝트명입니다."
          : "이미 사용 중인 프로젝트명입니다. (비공개 포함)";

        toast[isAvailable ? "success" : "error"](message);
      }
    } catch (error) {
      console.error("중복 체크 오류:", error);
      toast.error("중복 체크 중 오류가 발생했습니다.");
    }
  }, [formData.gbn, formData.orgNo, formData.projectNm]);

  // 다음 단계 이동 전 검증
  const validateAndProceed = useCallback(() => {
    const isNameChanged = projectInfo && projectInfo.projectNm !== formData.projectNm;
    const needsDuplicationCheck = !duplProjectNm && (!projectInfo || isNameChanged);

    if (!formData.projectNm) {
      toast.error("프로젝트명을 입력해 주세요.");
      return;
    }

    if (needsDuplicationCheck) {
      toast.error("프로젝트명 중복 확인을 해주세요.");
      return;
    }

    if (!formData.orgNo) {
      const orgType = formData.gbn === "C" ? "회사를" : "모임을";
      toast.error(`${orgType} 선택해주세요.`);
      return;
    }

    handleViewTypeChange();
  }, [duplProjectNm, projectInfo, formData.projectNm, formData.orgNo, formData.gbn]);

  // 파일 업로드
  const handleImportFile = async (e) => {
    const files = e.target.files;

    if (files) {
      const form = {
        content: [],
        files: [],
      };

      for (const item of files) {
        const id = uuid();
        const path = await readFileAsDataURL(item);

        form.content.push({ fileNm: item.name, type: item.type, path, id });
        form.files.push({ id, file: item });
      }

      setOrigin((data) => [...data, ...form.files]);
      setFiles((data) => [...data, ...form.content]);

      e.target.value = "";
    }
  };

  // 삭제 버튼 클릭
  const handleDeleteFile = (id) => {
    console.log(files);
    const fileToDelete = files.find((file) => file.id === id);

    if (!fileToDelete) return;

    setFiles((prev) => prev.filter((file) => file.id !== id));
    setOrigin((prev) => prev.filter((file) => file.id !== id));

    if (fileToDelete.projectFileNo) {
      setDeletedFiles((prev) => [...prev, fileToDelete]);
    }
  };

  // 업로드된 파일 저장
  const updateProjectFiles = async (projectNo, files) => {
    try {
      await axios.put("/api/file", {
        projectNo,
        refeNo: projectNo,
        gbn: "N",
        mberNo: user.mberNo,
        fileType: "F",
        delYn: "N",
        fileList: files,
        createMberId: user.userName,
        updateMberId: user.userName,
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      {modal}
      <ProjectSection>
        <ProjectCloseButton
          onClick={() =>
            navigatePath ? navigate(navigatePath, { state: { eventInfo } }) : navigate("/mypage")
          }>
          <IoMdClose />
        </ProjectCloseButton>
        <ProjectCreateSecContainer>
          <div className="inner">
            <h2>
              프로젝트 만들기 <span>STEP{viewType + 1}</span>
            </h2>
            <div className="createProjectContent">
              <div className="slideWrapper">
                <div
                  style={{ transform: `translateX(${transformValue}%)`, opacity: !viewType && 1 }}
                  className="slideContent">
                  <InputField
                    isAddDesc={isAddDesc}
                    setIsAddDesc={setIsAddDesc}
                    formData={formData}
                    changeInput={changeInput}
                    setFormData={setFormData}
                    handleFocus={handleFocus}
                    handleBlur={handleBlur}
                    projectInfo={projectInfo}
                    checkDuplProjectNm={checkDuplProjectNm}
                  />
                </div>
                <div
                  style={{ transform: `translateX(${transformValue}%)`, opacity: viewType && 1 }}
                  className="slideContent">
                  <Editor
                    formData={formData}
                    setFormData={setFormData}
                    changeInput={changeInput}
                    handleFocus={handleFocus}
                    handleBlur={handleBlur}
                    files={files}
                    handleImportFile={handleImportFile}
                    ckeditorInstanceRef={ckeditorInstanceRef}
                    handleDeleteFile={handleDeleteFile}
                  />
                </div>
              </div>
              <Preview formData={formData} focusState={focusState} isAddDesc={isAddDesc} />
            </div>
            <div className="moveAndSaveButtons">
              {!viewType ? (
                <button onClick={validateAndProceed}>다음</button>
              ) : (
                <button onClick={handleViewTypeChange}>이전</button>
              )}
              {viewType === 1 && <button onClick={checkValidation}>저장</button>}
            </div>
          </div>
        </ProjectCreateSecContainer>
      </ProjectSection>
    </>
  );
}
